// src/ItemVariantsForm.js ver 03 Dec 03 2025 9:47 pm - SB117
import React, { useState, useEffect, useCallback } from 'react'; // NEW v03: useCallback for memoizing generated name
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './App';
import * as itemVariantsService from './api/itemVariantsService';
import * as rawItemsService from './api/rawItemsService'; // To fetch active raw items for dropdown
import { ApiError } from './api/apiClient';

const initialItemVariantState = {
    AssociatedItem: '',
    AssociatedItemSeqId: '',
    Brand: '',
    Differentiator: '', // NEW
    ItemVariantName: '',
    ChefsRating: '3-Good quality',
    ChefsNotes: '', // NEW
    Status: 'Active',
};

// Allowed dropdown options (for frontend consistency)
const itemVariantStatuses = ['Active', 'Not-Active'];
const chefsRatings = ['1-Exceptional quality', '2-Observably Better', '3-Good quality', '4-Just below good but okay', '5-Questionable to Poor']; // NEW ver 02


function ItemVariantsForm() {
    // const { tenantId, id: itemVariantId } = useParams();
    const { id: itemVariantId } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(itemVariantId);
    const auth = useAuth();

    const [formData, setFormData] = useState(initialItemVariantState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [associatedItemOptions, setAssociatedItemOptions] = useState([]); // For AssociatedItem dropdown
    const [associatedItemError, setAssociatedItemError] = useState(null);
    const [generatedItemVariantName, setGeneratedItemVariantName] = useState(''); // NEW v03: To display generated name
    const [itemVariantNameError, setItemVariantNameError] = useState(null);

    // NEW v03: Helper to construct ItemVariantName (Frontend Helper - client-side prediction)
    const constructItemVariantName = useCallback((associatedItem, brand, differentiator) => {
        if (!associatedItem || !brand || !differentiator) return '';
        return `${associatedItem}_${brand}_${differentiator}`.replace(/ /g, '_').toUpperCase();
    }, []);


    const tenantId = auth.userProfile?.SubsCoDocId; // re-added ver01. existing in ver 00a
    console.log(`tenantId = ${tenantId}; itemVariantId = ${itemVariantId}`)


    useEffect(() => {
        const fetchData = async () => {
            if (!tenantId) {
                setError("Tenant ID missing in URL.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            setAssociatedItemError(null);

            try {
                const token = await auth.getIdToken();
                if (!token) {
                    setError("Authentication required. Please log in.");
                    setLoading(false);
                    return;
                }

                // Fetch active raw items for the dropdown
                const rawItemsResponse = await rawItemsService.getRawItems(tenantId, token, 1, 1000, 'Active'); // Fetch active raw items
                if (rawItemsResponse && rawItemsResponse.items) {
                    setAssociatedItemOptions(rawItemsResponse.items.map(item => ({
                        name: item.RawItemName,
                        seqId: item.SeqId,
                    })));
                } else {
                    setAssociatedItemError("Failed to load active raw items for dropdown.");
                }

                if (isEditing) {
                    const data = await itemVariantsService.getItemVariantById(tenantId, itemVariantId, token);
                    if (data) {
                        setFormData(data);
                        setGeneratedItemVariantName(data.ItemVariantName); // NEW v03: Set for display
                    } else {
                        setError("Item Variant not found.");
                        navigate(`/item-variants/${tenantId}`);
                    }
                } else {
                    // For new items, ensure a default raw item is pre-selected if options exist
                    if (rawItemsResponse && rawItemsResponse.items.length > 0) {
                        const defaultRawItem = rawItemsResponse.items[0];
                        // Create the object for setFormData first
                        const newFormData = {
                            ...initialItemVariantState, // Start with full initial state
                            AssociatedItem: defaultRawItem.RawItemName,
                            AssociatedItemSeqId: defaultRawItem.SeqId,
                        };
                        setFormData(newFormData); // Set the form data
                        
                        // Then use the *newFormData* to generate the name
                        setGeneratedItemVariantName(
                            constructItemVariantName(
                                newFormData.AssociatedItem, // NEW: Use newFormData
                                newFormData.Brand,          // NEW: Use newFormData
                                newFormData.Differentiator  // NEW: Use newFormData
                            )
                        );
                    }
               }
            } catch (err) {
                console.error("Error fetching data for item variant form:", err);
                setError(`Failed to load data: ${err.message}`);
                setAssociatedItemError(`Failed to load raw items: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [tenantId, itemVariantId, isEditing, navigate, auth, constructItemVariantName]);

    // NEW v03: Update generated name whenever relevant fields change
    useEffect(() => {
        setGeneratedItemVariantName(
            constructItemVariantName(
                formData.AssociatedItem,
                formData.Brand,
                formData.Differentiator
            )
        );
    }, [formData.AssociatedItem, formData.Brand, formData.Differentiator, constructItemVariantName]);





    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'AssociatedItem') {
            // Find the selected raw item to get its SeqId
            const selectedItem = associatedItemOptions.find(opt => opt.name === value);
            setFormData(prev => ({
                ...prev,
                [name]: value,
                AssociatedItemSeqId: selectedItem ? selectedItem.seqId : '',
            }));
        } 
        
        // else if (name === 'WithVariant') { // No WithVariant in ItemVariants, but keeping generic for future
        //     setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        // } else if (name === 'UnitPackagingQTY') {
        //     // Handle numeric input with 2 decimal places
        //     const floatValue = parseFloat(value);
        //     if (isNaN(floatValue)) {
        //         setFormData(prev => ({ ...prev, [name]: value })); // Allow empty or partial input
        //     } else {
        //         setFormData(prev => ({ ...prev, [name]: floatValue }));
        //     }
        // }

        else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setItemVariantNameError(null);
        setAssociatedItemError(null);

        if (!tenantId) {
            setError("Tenant ID missing. Cannot save item variant.");
            return;
        }
        // if (!formData.ItemVariantName || formData.ItemVariantName.trim() === '') {
        //     setItemVariantNameError("Item Variant Name is required.");
        //     return;
        // }
        if (!formData.AssociatedItem || formData.AssociatedItem.trim() === '' ||
            !formData.AssociatedItemSeqId || formData.AssociatedItemSeqId.trim() === '') {
            setAssociatedItemError("Associated Item and its ID are required.");
            return;
        }
        if (!formData.Brand || formData.Brand.trim() === '') {
            setError("Brand is required.");
            return;
        }
        if (!formData.Differentiator || formData.Differentiator.trim() === '') { // NEW validation
            setError("Differentiator is required.");
            return;
        }
        if (!formData.ChefsRating || !chefsRatings.includes(formData.ChefsRating)) {
            setError("Chefs Rating is required and must be a valid option.");
            return;
        }
        if (typeof formData.ChefsNotes !== 'string') { // NEW validation
            setError("Chefs Notes must be a string.");
            return;
        }
       
        try {
            const token = await auth.getIdToken();
            if (!token) {
                setError("Authentication required. Please log in.");
                return;
            }

            // const payload = { ...formData };  // NEW v03 - replaced by below
            const { ItemVariantName, ...payloadToSend } = formData; // NEW v03 - replaces above

            if (isEditing) {
                // await itemVariantsService.updateItemVariant(tenantId, itemVariantId, payload, token); // NEW V03: Replaced by below
                await itemVariantsService.updateItemVariant(tenantId, itemVariantId, payloadToSend, token); // NEW V03: Replaces above
                alert("Item Variant updated successfully!");
            } else {
                // await itemVariantsService.addItemVariant(tenantId, payload, token); // NEW V03: Replaced by below
                await itemVariantsService.addItemVariant(tenantId, payloadToSend, token); // NEW V03: Replaces above
                alert("Item Variant created successfully!");
            }
            // navigate(`/item-variants/tenant/${tenantId}`);
            navigate(`/item-variants`);
        } catch (err) {
            console.error("Error saving item variant via Cloud Function:", err);
            if (err instanceof ApiError && err.status === 409) {
                setItemVariantNameError(`Error: ${err.message}`);
                setError(null);
            } else {
                setError(`Failed to save item variant: ${err.message}`);
            }
        }
    };

    if (loading) return <p>Loading item variant details...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!tenantId) return <p className="container" style={{ color: 'red' }}>Error: Tenant ID is required to add/edit item variant.</p>;


    return (
        <div className="container form-container">
            <h2>{isEditing ? `Edit Item Variant (Tenant: ${tenantId})` : `Add New Item Variant (Tenant: ${tenantId})`}</h2>
            <form onSubmit={handleSubmit}>
                {isEditing && formData.SeqId && (
                    <div className="form-group">
                        <label>Sequential ID</label>
                        <input type="text" name="SeqId" value={formData.SeqId || ''} disabled />
                    </div>
                )}

                <div className="form-group">
                    <label>Item</label> {/* Label for raw item dropdown */}
                    <select
                        name="AssociatedItem"
                        value={formData.AssociatedItem}
                        onChange={handleChange}
                        required
                        disabled={associatedItemOptions.length === 0 && !associatedItemError && loading}
                    >
                        <option value="">-- Select an Item --</option>
                        {associatedItemOptions.map(item => (
                            <option key={item.seqId} value={item.name}>{item.name}</option>
                        ))}
                    </select>
                    {associatedItemError && <p style={{ color: 'red', fontSize: '0.85em' }}>{associatedItemError}</p>}
                </div>

                <div className="form-group">
                    <label>Item-Id</label> {/* Display associated SeqId */}
                    <input
                        type="text"
                        name="AssociatedItemSeqId"
                        value={formData.AssociatedItemSeqId}
                        disabled // System-generated based on item selection
                    />
                </div>

                <div className="form-group">
                    <label>Brand</label>
                    <input type="text" name="Brand" value={formData.Brand} onChange={handleChange} required />
                </div>

                <div className="form-group"> {/* NEW: Differentiator */}
                    <label>Differentiator</label>
                    <input type="text" name="Differentiator" value={formData.Differentiator} onChange={handleChange} required />
                </div>

                {/* <div className="form-group">
                    <label>Item Variant Name</label>
                    <input
                        type="text"
                        name="ItemVariantName"
                        value={formData.ItemVariantName}
                        onChange={handleChange}
                        required
                    />
                    {itemVariantNameError && <p style={{ color: 'red', fontSize: '0.85em' }}>{itemVariantNameError}</p>}
                </div> */}

                <div className="form-group"> {/* NEW: Item Variant Name (Auto-generated and disabled) */}
                    <label>Item Variant Name</label>
                    <input type="text" name="ItemVariantName" value={generatedItemVariantName || ''} disabled />
                    {itemVariantNameError && <p style={{ color: 'red', fontSize: '0.85em' }}>{itemVariantNameError}</p>}
                </div>

                <div className="form-group"> {/* NEW: Chefs Rating dropdown */}
                    <label>Chefs Rating</label>
                    <select name="ChefsRating" value={formData.ChefsRating} onChange={handleChange} required>
                        {chefsRatings.map(rating => (
                            <option key={rating} value={rating}>{rating}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group"> {/* NEW: Chefs Notes */}
                    <label>Chefs Notes</label>
                    <textarea name="ChefsNotes" value={formData.ChefsNotes} onChange={handleChange} rows="3"></textarea>
                </div>

                <div className="form-group">
                    <label>Status</label>
                    <select name="Status" value={formData.Status} onChange={handleChange} required>
                        {itemVariantStatuses.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>

                {isEditing && formData.DateAdded && (
                    <div className="form-group">
                        <label>Date Added</label>
                        <input type="text" value={new Date(formData.DateAdded._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}
                {isEditing && formData.DateLastModified && (
                    <div className="form-group">
                        <label>Date Last Modified</label>
                        <input type="text" value={new Date(formData.DateLastModified._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}

                <button type="submit">Save Item Variant</button>
            </form>
        </div>
    );
}

export default ItemVariantsForm;